import React, { useContext, useEffect, useState } from "react";
import { ActionType, CompletePaymentStepPayload } from "../../AppDataProvider/actions";
import { AppContext, BookingData } from "../../AppDataProvider/AppDataProvider";
import { LoadingPill as BaseLoadingPill, NavButtons, Notification, Radio } from "../../common/components";
import { absoluteCentre, marginBottom, marginLeft, marginRight, marginTop } from "../../common/styles";
import { useCreateBookingsForBookingToolMutation, useCreateBookingsWithUnpaidInvoiceMutation, useGetOrganisationPaymentQuery } from "../../GeneratedGraphQL";
import { getBookingQuestionInput, getCrmFieldBookingInput, getCurrencyDisplay, getPaymentIntegrationType, isFeatureFlagEnabledForOrganisation, requireBookingDataFields } from "../../utils";
import { PartRequired, PaymentIntegrationType } from "../../utils/types";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AdyenPayment } from "./AdyenPayment/AdyenPayment";
import { DiscountCode } from "./DiscountCode/DiscountCode";
import { SquarePayment } from "./SquarePayment/SquarePayment";
import { StripePayment } from "./StripePayment/StripePayment";
import { SupportedCards } from "./SupportedCards/SupportedCards";
type PaymentsStepBookingData = PartRequired<BookingData, "createdSource" | "organisation" | "reservedSpaceId" | "service" | "timezone" | "userDetails">;
export function PaymentsStep(): React.ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [payNow, setPayNow] = useState<boolean>(true);
  const [discountCodeLoading, setDiscountCodeLoading] = useState<boolean>(false);
  const [navigationDisabled, setNavigationDisabled] = useState(false);
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);
  requireBookingDataFields<PaymentsStepBookingData>(appData.bookingData, ["createdSource", "organisation", "reservedSpaceId", "timezone", "userDetails", "service"]);
  const {
    createdSource,
    organisation,
    timezone,
    reservedSpaceId,
    service,
    userDetails,
    discountCode,
    tier,
    pricePoints,
    bookingNotes,
    bookingQuestionData
  } = appData.bookingData;
  const {
    crmFieldData
  } = userDetails;
  const selectedBookings = appData.bookingData.selectedBookings;
  const showDiscountCode = service.isDiscountCodeAvailable ?? false;
  const paymentsIntegrationType = getPaymentIntegrationType(organisation);
  if (!paymentsIntegrationType) {
    return <ErrorNotification variant="warning" role="alert">
        {t("errors.noPaymentIntegration")}
      </ErrorNotification>;
  }
  const [createBookingsWithUnpaidInvoice] = useCreateBookingsWithUnpaidInvoiceMutation({
    errorPolicy: "all"
  });
  const [createBookingsForBookingTool] = useCreateBookingsForBookingToolMutation({
    errorPolicy: "all"
  });
  // Ignore lack of test coverage for locale "cimode" as it will only have "cimode" when in test mode
  /* istanbul ignore next */
  const locale = i18n.language !== "cimode" ? i18n.language : "en-GB";
  const {
    data,
    loading,
    error: paymentError,
    refetch
  } = useGetOrganisationPaymentQuery({
    fetchPolicy: "network-only",
    variables: {
      bookingReservations: [{
        reservedSpaceId: reservedSpaceId,
        timezone: timezone,
        ...(selectedBookings && {
          bookingIds: selectedBookings.map(booking => booking._id)
        }),
        ...(tier && {
          tierId: tier._id
        }),
        ...(pricePoints && {
          pricePoints: pricePoints.map(pricePoint => {
            return {
              pricePointId: pricePoint.pricePoint._id,
              quantity: pricePoint.quantity
            };
          })
        })
      }],
      id: organisation._id,
      locale,
      ...(discountCode && {
        discount: {
          customerEmail: userDetails.email,
          discountCode
        }
      })
    }
  });
  useEffect(() => {
    if (paymentError) {
      /* istanbul ignore next */
      const retryPaymentQuery = () => void refetch();
      /* istanbul ignore next */
      const reload = () => location.reload();
      const dataHasChanged = paymentError.graphQLErrors.some(error => error.extensions?.code === "BAD_USER_INPUT");
      appDataDispatch({
        payload: {
          buttonLabel: dataHasChanged ? t("reload") : t("tryAgain"),
          content: dataHasChanged ? t("errors.somethingChanged") : t("errors.paymentError"),
          icon: faExclamationTriangle,
          onClick: dataHasChanged ? reload : retryPaymentQuery,
          title: dataHasChanged ? t("somethingChanged") : t("serverError")
        },
        type: ActionType.SHOW_MODAL_DIALOG
      });
      console.error({
        paymentError
      });
    }
  }, [appDataDispatch, paymentError, refetch, t]);
  if (loading) {
    return <LoadingPill />;
  }
  if (paymentError) {
    return <ErrorNotification variant="warning" role="alert">
        {t("errors.paymentError")}
      </ErrorNotification>;
  }
  if (!data) {
    return <ErrorNotification variant="warning" role="alert">
        {t("errors.generic")}
      </ErrorNotification>;
  }
  if (!data.organisation) {
    return <ErrorNotification variant="warning" role="alert">
        {t("errors.noPayment")}
      </ErrorNotification>;
  }
  const {
    paymentPrice,
    paymentPriceInPence,
    isPaymentOptional,
    minChargeAdjustmentValue,
    totalPriceInPence,
    appliedDiscountValue
  } = data.organisation.payment;
  const displayPrice = getCurrencyDisplay(paymentPrice, organisation.currency, i18n.language);
  const isFullyDiscounted = paymentPriceInPence === 0 && appliedDiscountValue && appliedDiscountValue > 0;
  const completePaymentStep = (bookingIds?: string[]) => {
    const payload: CompletePaymentStepPayload = {
      updatedData: {
        ...(bookingIds && bookingIds.length > 0 && {
          confirmedBookingIds: bookingIds
        }),
        payNow: payNow,
        totalPaid: paymentPrice
      }
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_PAYMENTS_STEP
    });
  };
  const onGoBack = () => {
    appDataDispatch({
      type: ActionType.BACK_FROM_PAYMENTS_STEP
    });
  };

  /**
   * Is used to confirm bookings that are Fully discounted
   * or a discount code is applied with pay later selected.
   */
  const confirmBookingUnpaidInvoice = async () => {
    const {
      data: createdBookings,
      errors: createBookingsResultErrors
    } = await createBookingsWithUnpaidInvoice({
      variables: {
        bookings: [{
          ...(bookingNotes && {
            bookingNotes
          }),
          bookingQuestions: getBookingQuestionInput({
            bookingQuestionData,
            bookingQuestions: organisation.externalBookingQuestions,
            pickedServiceId: service._id
          }),
          customer: {
            ...userDetails,
            crmFieldData: getCrmFieldBookingInput(crmFieldData)
          },
          ...(tier && {
            tierId: tier._id
          }),
          ...(pricePoints && {
            pricePoints: pricePoints.map(pricePoint => {
              return {
                pricePointId: pricePoint.pricePoint._id,
                quantity: pricePoint.quantity
              };
            })
          }),
          organisationId: organisation._id,
          reservedSpaceId: reservedSpaceId,
          timezone: timezone
        }],
        ...(!appData.bookingTool.isGenericBookingTool && {
          bookingToolId: appData.bookingTool._id
        }),
        createdSource,
        locale,
        ...(discountCode && {
          discount: {
            customerEmail: userDetails.email,
            discountCode
          }
        })
      }
    });
    if (!createdBookings) {
      console.error({
        createBookingsResultErrors
      });
      if (createBookingsResultErrors?.some(error => error.extensions?.code === "BAD_USER_INPUT")) {
        appDataDispatch({
          payload: {
            buttonLabel: t("reload"),
            content: t("errors.somethingChanged"),
            icon: faExclamationTriangle,
            onClick: /* istanbul ignore next */() => location.reload(),
            title: t("somethingChanged")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
      setError(t("errors.confirmBookingError"));
      return;
    }
    completePaymentStep(createdBookings.createBookingsWithUnpaidInvoice.bookings.map(booking => booking._id));
  };

  /**
   * Is used when we confirm a pay later booking.
   * Will not create an invoice and discount codes
   * are not applied.
   */
  const confirmPayLaterBooking = async () => {
    const {
      data: createdBookings,
      errors: createBookingsResultErrors
    } = await createBookingsForBookingTool({
      variables: {
        bookings: [{
          ...(bookingNotes && {
            bookingNotes
          }),
          bookingQuestions: getBookingQuestionInput({
            bookingQuestionData,
            bookingQuestions: organisation.externalBookingQuestions,
            pickedServiceId: service._id
          }),
          customer: {
            ...userDetails,
            crmFieldData: getCrmFieldBookingInput(crmFieldData)
          },
          ...(tier && {
            tierId: tier._id
          }),
          ...(pricePoints && {
            pricePoints: pricePoints.map(pricePoint => {
              return {
                pricePointId: pricePoint.pricePoint._id,
                quantity: pricePoint.quantity
              };
            })
          }),
          organisationId: organisation._id,
          reservedSpaceId: reservedSpaceId,
          timezone: timezone
        }],
        ...(!appData.bookingTool.isGenericBookingTool && {
          bookingToolId: appData.bookingTool._id
        }),
        createdSource,
        locale
      }
    });
    if (!createdBookings) {
      if (createBookingsResultErrors?.some(error => error.extensions?.code === "BAD_USER_INPUT")) {
        appDataDispatch({
          payload: {
            buttonLabel: t("reload"),
            content: t("errors.somethingChanged"),
            icon: faExclamationTriangle,
            onClick: /* istanbul ignore next */() => location.reload(),
            title: t("somethingChanged")
          },
          type: ActionType.SHOW_MODAL_DIALOG
        });
      }
      console.error({
        createBookingsResultErrors
      });
      setError(t("errors.confirmBookingError"));
      return;
    }
    completePaymentStep(createdBookings.createBookingsForBookingTool.bookings.map(booking => booking._id));
  };
  const onDiscountCodeLoading = (loading: boolean) => {
    setDiscountCodeLoading(loading);
  };
  const adyenFeatureFlagEnabled = isFeatureFlagEnabledForOrganisation("adyen-payment-enhancements", organisation);
  return <Container data-sentry-element="Container" data-sentry-component="PaymentsStep" data-sentry-source-file="PaymentsStep.tsx">
      {showDiscountCode && <DiscountCode onLoadingCallback={onDiscountCodeLoading} totalPriceInPence={totalPriceInPence} />}

      {isFullyDiscounted && <Notification variant="success">{t("fullDiscount")}</Notification>}

      {!discountCodeLoading && !loading && <>
          {minChargeAdjustmentValue && minChargeAdjustmentValue > 0 && <Notification variant="info">
              {t("discountCodeMinChargeText", {
          price: displayPrice
        })}
            </Notification>}

          {isPaymentOptional && paymentPrice > 0 && <OptionalPaymentContainer>
              <PaymentOption>
                <Radio id="payNow" name="paymentOption" value={"payNow"} checked={payNow} onChange={() => {
            setPayNow(true);
          }} />
                <PaymentOptionLabel htmlFor="payNow">
                  {t("payNow")}
                </PaymentOptionLabel>
              </PaymentOption>
              <PaymentOption>
                <Radio id="payLater" name="paymentOption" value={"payLater"} checked={!payNow} onChange={() => {
            setPayNow(false);
          }} />
                <PaymentOptionLabel htmlFor="payLater">
                  {t("payLater")}
                </PaymentOptionLabel>
              </PaymentOption>
            </OptionalPaymentContainer>}

          {!isFullyDiscounted && payNow && <CardOrDebit>
              <CardOrDebitText>
                {t("payWithCreditOrDebit", {
            price: displayPrice
          })}
              </CardOrDebitText>
              <SupportedCards />
            </CardOrDebit>}

          {adyenFeatureFlagEnabled && payNow && paymentsIntegrationType === PaymentIntegrationType.ADYEN && paymentPriceInPence > 0 && <AdyenPayment onComplete={completePaymentStep} value={paymentPriceInPence} />}

          {payNow && paymentsIntegrationType === PaymentIntegrationType.STRIPE && paymentPriceInPence > 0 && <StripePayment stripeIntegration={organisation.stripeIntegration} onGoBack={onGoBack} onPayAndBook={completePaymentStep} />}

          {payNow && organisation.squareIntegration && paymentPriceInPence > 0 && <SquarePayment priceToPay={paymentPrice} locationId={organisation.squareIntegration.locationId} onGoBack={onGoBack} onPayAndBook={completePaymentStep} />}

          {error && <ErrorNotification variant="warning">{error}</ErrorNotification>}

          {(isFullyDiscounted || !payNow) && <NavButtons {...{
        continueLabel: t("bookNow"),
        navigationDisabled: navigationDisabled,
        onBack: onGoBack,
        onContinue: () => {
          const continueFn = isFullyDiscounted || discountCode ? confirmBookingUnpaidInvoice : confirmPayLaterBooking;
          void (async () => {
            setNavigationDisabled(true);
            await continueFn();
            setNavigationDisabled(false);
          })();
        }
      }} />}
        </>}
    </Container>;
}
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-self: center;

  width: 100%;
  ${marginLeft.m}
  ${marginRight.m}
`;
const CardOrDebit = styled.div`
  display: flex;
  flex-direction: column;

  ${marginTop.m}
  ${marginBottom.m}
`;
const CardOrDebitText = styled.div`
  ${marginBottom.m}
`;
const OptionalPaymentContainer = styled.div`
  ${marginTop.s}
  ${marginBottom.m}
`;
const PaymentOption = styled.div`
  display: flex;
  align-items: center;
  ${marginBottom.s}
`;
const PaymentOptionLabel = styled.label`
  ${marginLeft.xs}
`;
const LoadingPill = styled(BaseLoadingPill)`
  ${absoluteCentre}
`;
const ErrorNotification = styled(Notification)`
  ${marginTop.m}
`;
export default PaymentsStep;